import { memo, useCallback, useEffect, useState, useRef } from 'react'
import { GlassButton } from '@/components/ui/GlassButton'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Trash2, ShoppingCart, Eye, CreditCard } from 'lucide-react'
import { useCart } from '@/stores/useCart'

export const CartDropdown = memo(() => {
  const [mounted, setMounted] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { items, isOpen, totalItems, totalPrice, removeItem, setIsOpen } =
    useCart()

  useEffect(() => {
    setMounted(true)
  }, [])

  // Close dropdown when clicking outside
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    },
    [setIsOpen]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  if (!isOpen || !mounted) {
    return null
  }

  const dropdownContent = (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: '100px',
        right: '24px',
        width: '360px',
        zIndex: 2147483647,
        pointerEvents: 'auto',
        isolation: 'isolate',
      }}
    >
      <Card
        className="shadow-2xl border-border/50 bg-background/95 backdrop-blur-xl"
        style={{ zIndex: 2147483647, position: 'relative' }}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Warenkorb</h3>
            <span className="text-sm text-muted-foreground">
              {totalItems} Artikel
            </span>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-brand-purple/10 via-brand-pink/10 to-brand-blue/10 flex items-center justify-center">
                <ShoppingCart className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">Ihr Warenkorb ist leer</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {items.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center space-x-3 p-3 rounded-lg bg-gradient-to-r from-brand-purple/5 via-brand-pink/5 to-brand-blue/5 border border-brand-purple/20 hover:border-brand-purple/30 transition-all duration-200"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-brand-purple/10 via-brand-pink/10 to-brand-blue/10 rounded-lg flex items-center justify-center border border-brand-purple/20">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-brand-purple/30 rounded" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate text-foreground">
                        {item.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {item.price.toFixed(2)}€ × {item.quantity}
                      </p>
                    </div>
                    <GlassButton
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(item.id)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-200"
                    >
                      <Trash2 className="h-4 w-4" />
                    </GlassButton>
                  </div>
                ))}
              </div>

              <Separator className="my-4 bg-brand-purple/20" />

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Gesamt:</span>
                  <span className="font-semibold text-foreground">
                    {totalPrice.toFixed(2)}€
                  </span>
                </div>
                <div className="flex space-x-2">
                  <GlassButton
                    variant="secondary"
                    size="md"
                    className="flex-1 h-10"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Warenkorb
                  </GlassButton>
                  <GlassButton
                    variant="primary"
                    size="md"
                    className="flex-1 h-10"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Kasse
                  </GlassButton>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )

  return dropdownContent
})

CartDropdown.displayName = 'CartDropdown'
